import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { InteractionRequiredAuthError, PublicClientApplication, type AccountInfo } from '@azure/msal-browser';
import { useQueryClient } from '@tanstack/react-query';
import { api, setTokenProvider, setUnauthenticatedHandler } from '../api/client';
import { ApiError, describeError } from '../api/errors';
import { env } from '../env';
import { AuthContext, type AuthState, type AuthStatus } from './AuthContext';

/**
 * Anmeldung: Entra über MSAL (eigene Autopilot-App, Cache in sessionStorage) oder
 * Dev-Modus (Token von POST /auth/dev/token). Tokens liegen nur im Speicher bzw. im
 * MSAL-sessionStorage-Cache; in localStorage wird nichts abgelegt. Im Dev-Modus wird nur
 * die gewählte Dev-E-Mail (kein Geheimnis) in sessionStorage gemerkt, um nach Reload
 * ein neues Token zu holen.
 */
const DEV_EMAIL_KEY = 'autopilot.dev.email';

function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

let msalInstance: PublicClientApplication | null = null;
function getMsal(): PublicClientApplication {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication({
      auth: {
        clientId: env.entraClientId,
        authority: `https://login.microsoftonline.com/${env.entraTenantId || 'organizations'}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
      },
      cache: { cacheLocation: 'sessionStorage' },
    });
  }
  return msalInstance;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('initializing');
  const [accountLabel, setAccountLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const devToken = useRef<{ token: string; email: string; exp: number } | null>(null);
  const account = useRef<AccountInfo | null>(null);

  const loginDev = useCallback(async (email: string) => {
    setStatus('signingIn');
    setError(null);
    try {
      const res = await api.devToken(email);
      const token = res.accessToken ?? res.token;
      if (!token) throw new ApiError(500, null);
      devToken.current = { token, email, exp: Date.now() + (res.expiresIn ?? 3600) * 1000 - 30000 };
      safeSession()?.setItem(DEV_EMAIL_KEY, email);
      setAccountLabel(email);
      setStatus('signedIn');
    } catch (err) {
      devToken.current = null;
      safeSession()?.removeItem(DEV_EMAIL_KEY);
      const d = describeError(err, 'Anmeldung');
      setError(err instanceof ApiError && err.status === 403 ? 'Für dieses Konto ist kein Autopilot-Zugang eingerichtet.' : `${d.message} ${d.next}`);
      setStatus('signedOut');
    }
  }, []);

  const loginMsal = useCallback(async () => {
    setError(null);
    setStatus('signingIn');
    await getMsal().loginRedirect({ scopes: env.apiScope ? [env.apiScope] : [] });
  }, []);

  const logout = useCallback(async () => {
    qc.clear();
    if (env.authMode === 'dev') {
      devToken.current = null;
      safeSession()?.removeItem(DEV_EMAIL_KEY);
      setAccountLabel(null);
      setStatus('signedOut');
      return;
    }
    const acc = account.current;
    account.current = null;
    await getMsal().logoutRedirect({ account: acc ?? undefined });
  }, [qc]);

  // Tokenlieferant für den API-Client
  useEffect(() => {
    setTokenProvider(async () => {
      if (env.authMode === 'dev') {
        const t = devToken.current;
        if (!t) return null;
        if (Date.now() > t.exp) {
          const res = await api.devToken(t.email);
          const token = res.accessToken ?? res.token ?? null;
          if (token) devToken.current = { token, email: t.email, exp: Date.now() + (res.expiresIn ?? 3600) * 1000 - 30000 };
          return token;
        }
        return t.token;
      }
      const acc = account.current;
      if (!acc) return null;
      const scopes = env.apiScope ? [env.apiScope] : [];
      try {
        const r = await getMsal().acquireTokenSilent({ scopes, account: acc });
        return r.accessToken;
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          await getMsal().acquireTokenRedirect({ scopes, account: acc });
        }
        return null;
      }
    });
    setUnauthenticatedHandler(() => {
      if (env.authMode === 'dev') {
        devToken.current = null;
        setStatus('signedOut');
        setError('Ihre Anmeldung ist abgelaufen. Bitte melden Sie sich erneut an.');
      }
    });
    return () => {
      setTokenProvider(null);
      setUnauthenticatedHandler(null);
    };
  }, []);

  // Initialisierung
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (env.authMode === 'dev') {
        const email = safeSession()?.getItem(DEV_EMAIL_KEY);
        if (email) await loginDev(email);
        else if (!cancelled) setStatus('signedOut');
        return;
      }
      if (!env.entraClientId) {
        setError('Die Entra-Anmeldung ist nicht konfiguriert (VITE_ENTRA_CLIENT_ID fehlt).');
        setStatus('error');
        return;
      }
      try {
        const msal = getMsal();
        await msal.initialize();
        const result = await msal.handleRedirectPromise();
        const acc = result?.account ?? msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
        if (acc) {
          msal.setActiveAccount(acc);
          account.current = acc;
          if (!cancelled) {
            setAccountLabel(acc.name ?? acc.username);
            setStatus('signedIn');
          }
        } else if (!cancelled) setStatus('signedOut');
      } catch {
        if (!cancelled) {
          setError('Die Anmeldung bei Microsoft Entra ist fehlgeschlagen. Bitte versuchen Sie es erneut.');
          setStatus('signedOut');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loginDev]);

  const value = useMemo<AuthState>(
    () => ({ status, mode: env.authMode, accountLabel, error, loginDev, loginMsal, logout }),
    [status, accountLabel, error, loginDev, loginMsal, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
